import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { addDays, isBefore, startOfDay, format } from "date-fns";
import { parseScheduleDateLocal } from "../utils/dateUtils";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { getPropertyGroups } from "../../property/api/actions";

function DisconnectScheduleForm(props) {
  const [formData, setFormData] = useState({
    groupId: "",
    scheduledDisconnectDate: moment(addDays(new Date(), 1)),
    notes: "",
    isActive: 1,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (props.open) {
      // Fetch all property groups for dropdown (use high limit to get all groups)
      props.getPropertyGroups({ limit: 10000, page: 1 });
      
      if (props.editingSchedule) {
        let scheduleDate = moment(addDays(new Date(), 1));
        const parsed = parseScheduleDateLocal(props.editingSchedule.scheduledDisconnectDate);
        if (parsed) {
          scheduleDate = moment(parsed);
        }
        setFormData({
          groupId: props.editingSchedule.groupId || "",
          scheduledDisconnectDate: scheduleDate,
          notes: props.editingSchedule.notes || "",
          isActive: props.editingSchedule.isActive !== undefined ? props.editingSchedule.isActive : true,
        });
      } else {
        // Reset form for new schedule
        setFormData({
          groupId: "",
          scheduledDisconnectDate: moment(addDays(new Date(), 1)),
          notes: "",
          isActive: 1,
        });
      }
      setErrors({});
    }
  }, [props.open, props.editingSchedule]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.groupId) {
      newErrors.groupId = "Please select a property group";
    }
    
    const scheduleDate = formData.scheduledDisconnectDate ? (moment.isMoment(formData.scheduledDisconnectDate) ? formData.scheduledDisconnectDate.toDate() : formData.scheduledDisconnectDate) : null;
    if (!scheduleDate || isBefore(scheduleDate, startOfDay(new Date()))) {
      newErrors.scheduledDisconnectDate = "Please select a future date";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const scheduleDate = moment.isMoment(formData.scheduledDisconnectDate) ? formData.scheduledDisconnectDate.toDate() : formData.scheduledDisconnectDate;
    const scheduleData = {
      groupId: parseInt(formData.groupId),
      scheduledDisconnectDate: format(startOfDay(scheduleDate), "yyyy-MM-dd HH:mm:ss"),
      notes: formData.notes,
      isActive: formData.isActive,
    };

    if (props.editingSchedule) {
      scheduleData.uuid = props.editingSchedule.uuid;
      props.onSave(scheduleData, true); // true for update
    } else {
      props.onSave(scheduleData, false); // false for create
    }
  };

  const propertyGroups = props.properties?.propertyGroups?.items || [];

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Dialog 
        open={props.open} 
        onClose={props.onClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {props.editingSchedule ? "Edit Disconnect Schedule" : "New Disconnect Schedule"}
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <FormControl fullWidth error={!!errors.groupId}>
              <InputLabel>Property Group</InputLabel>
              <Select
                value={formData.groupId}
                onChange={(e) => handleInputChange("groupId", e.target.value)}
                label="Property Group"
              >
                {propertyGroups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: group.bgColorTag ? `#${group.bgColorTag}` : "#ccc",
                        }}
                      />
                      <Typography variant="body2">
                        {group.name}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {errors.groupId && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {errors.groupId}
                </Typography>
              )}
            </FormControl>

            <DatePicker
              label="Scheduled Disconnect Date"
              value={formData.scheduledDisconnectDate}
              onChange={(date) => handleInputChange("scheduledDisconnectDate", date)}
              minDate={moment(addDays(new Date(), 1))}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors.scheduledDisconnectDate,
                  helperText: errors.scheduledDisconnectDate || "Select a future date for the disconnect",
                },
              }}
            />

            <TextField
              label="Notes"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => handleInputChange("notes", e.target.value)}
              placeholder="Optional notes about this disconnect schedule..."
            />

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.isActive}
                onChange={(e) => handleInputChange("isActive", e.target.value)}
                label="Status"
              >
                <MenuItem value={1}>Active</MenuItem>
                <MenuItem value={0}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={props.onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={props.isLoading}
          >
            {props.isLoading ? "Saving..." : props.editingSchedule ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}

DisconnectScheduleForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  editingSchedule: PropTypes.object,
  isLoading: PropTypes.bool,
  properties: PropTypes.object.isRequired,
  getPropertyGroups: PropTypes.func.isRequired,
};

function State(state) {
  return {
    properties: state.properties,
  };
}

export default connect(State, { getPropertyGroups })(DisconnectScheduleForm);
